import { HttpErrorResponse } from '@angular/common/http';
import { Component, Injector, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AumBreadcrumbComponentItem, IPageHeader } from '@modeso/aumico-lib-core-fe';
import { OrganizationProvider, updateCurrentOrganization } from '@modeso/aumico-lib-users-fe';
import { GetOrganizationDto } from '@modeso/aumico-lib-users-fe/src/lib/dtos/getOrganizationDto.interface';
import { INameId } from '@modeso/types__aumico-lib-core-be';
import { OrganizationDTO } from '@modeso/types__aumico-lib-users-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import fastDeepEqual from 'fast-deep-equal/es6';
import * as countries from 'i18n-iso-countries';
import { cloneDeep } from 'lodash-es';
import { Observable, Subject, of } from 'rxjs';
import { RouteLabels } from '../../../enums/routeLabels.enum';
import { BasePageComponent } from '../../../pages/base.page';
import { CountriesMapper } from '../../../shared/mappers/countries.mapper';
import { CountriesService } from '../../../shared/services/countries.service';

@UntilDestroy()
@Component({
    selector: 'app-page-organization-profile.page',
    templateUrl: './organization-profile.page.html',
    styleUrls: ['./organization-profile.page.scss'],
})
export class OrganizationProfilePage extends BasePageComponent implements OnInit, OnDestroy {
    private initialOrganizationProfile: OrganizationDTO;
    private modifiedOrganizationProfile: OrganizationDTO;
    private currentLanguage = this.getCurrentLanguageWithFallBack();
    countries: INameId[];

    readonly proceedButtonEvent$ = new Subject<void>();

    pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.ORGANIZATION_PROFILE,
    };

    saveOrganization = new Subject<void>();
    breadcrumbItems: AumBreadcrumbComponentItem[] = [
        {
            displayName: 'Your Organization',
            translated: true,
        },
        {
            displayName: '',
            translated: true,
        },
    ];

    constructor(
        injector: Injector,
        dialog: MatDialog,
        private countriesService: CountriesService,
        private countriesMapper: CountriesMapper,
        private organizationProvider: OrganizationProvider,
    ) {
        super(injector, dialog);
        this.disableProceedButton = false;
        this.onSaveSuccessfully = this.onSaveSuccessfully.bind(this);
    }

    override ngOnInit() {
        this.actionsListener$
            .pipe(ofType(updateCurrentOrganization.success), untilDestroyed(this))
            .subscribe(this.onSaveSuccessfully);

        this.actionsListener$
            .pipe(ofType(updateCurrentOrganization.failure), untilDestroyed(this))
            .subscribe((response) => this.handleErrors(response.payload));

        this.prepareCountriesDropDownOptions();

        this.organizationProvider
            .getCurrentOrganizationName$()
            .pipe(untilDestroyed(this))
            .subscribe((name: string) => {
                if (this.breadcrumbItems.length > 1) {
                    this.breadcrumbItems[1].displayName = name;
                }
            });
        super.ngOnInit();
    }

    private prepareCountriesDropDownOptions(): void {
        const retrievedCountriesByUSerLanguage: countries.LocalizedCountryNames<{ select: 'official' }> =
            this.countriesService.getCountriesByLanguage(this.currentLanguage);
        this.countries = this.countriesMapper.mapCountries(retrievedCountriesByUSerLanguage);
    }

    onInitialOrganizationProfile(organizationProfileChanges: OrganizationDTO): void {
        this.initialOrganizationProfile = organizationProfileChanges;
    }

    onOrganizationProfileChange(organizationProfileChanges: OrganizationDTO): void {
        this.modifiedOrganizationProfile = organizationProfileChanges;
    }

    saveButtonClicked = (): void => {
        this.proceedButtonEvent$.next();
    };

    protected override isDataChanged(): Observable<boolean> {
        // we make a comparison between "this.organizationProfile" which is the currently saved one
        // and "modifiedOrganizationProfile" which is the possibly modified one
        // we only do the preparation for saving to have the same data structure that will be saved
        const isModifiedOrganizationProfileEqualInitialOrganizationProfile = fastDeepEqual(
            this.modifiedOrganizationProfile,
            this.initialOrganizationProfile,
        );
        return of(!isModifiedOrganizationProfileEqualInitialOrganizationProfile);
    }

    onSaveSuccessfully(): void {
        this.savedSuccessfully.next(true);
        this.initialOrganizationProfile = this.modifiedOrganizationProfile;
        const savedOrganization: GetOrganizationDto = cloneDeep<GetOrganizationDto>(this.modifiedOrganizationProfile);
        this.organizationProvider.setCurrentOrganization(savedOrganization);
    }

    handleErrors(error: HttpErrorResponse): void {
        if (error != null) {
            this.savedSuccessfully.next(false);
        }
    }

    override save(): Observable<void> {
        this.saveOrganization.next();
        return of(void 0);
    }
}
